import { useEffect, useState } from "react";
import { Shield, Lock, Eye, FileText, Users, Scale, AlertTriangle, Globe, Cookie, RefreshCw, Mail, MapPin, BookOpen, Gavel, FileCheck, Building, UserCheck, Database, Server, CheckCircle, ArrowUp } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const sections = [
  {
    id: "introducao",
    icon: Shield,
    title: "Proteção de seus Dados Pessoais",
    content: [
      {
        text: "Os dados que serão coletados são de uso exclusivo da AROAN - Eleeva IT. Os dados estão protegidos e não serão divulgados ou compartilhados com terceiros. Garantimos total segurança. Nos baseamos nas leis civis de proteção de dados. Todo e qualquer dado é armazenado em bancos confiáveis e seguros. Disparo de e-mails marketing são feitos de acordo com seu consentimento. Sendo assim, você pode pedir para que sejam retirados seus dados do cadastro de envio a qualquer momento.",
      },
    ],
  },
  {
    id: "termo",
    icon: FileText,
    title: "Termo de Uso e Política de Privacidade",
    content: [
      {
        text: "Neste Termo de Uso e Política de Privacidade, você encontrará informações sobre o funcionamento do serviço solicitado, fornecido por meio de aplicações no site, sistemas e aplicativos para dispositivos móveis e as regras aplicáveis a ele; o embasamento legal relacionado à prestação do serviço; as suas responsabilidades ao utilizar o serviço; as responsabilidades da administração ao fornecer o serviço; informações para contato, caso exista alguma dúvida ou seja necessário atualizar informações; e o foro responsável por eventuais reclamações, caso questões deste documento tenham sido violadas.",
      },
      {
        text: "Além disso, você encontrará informações sobre qual o tratamento dos dados pessoais realizados, de forma automatizada ou não, e a sua finalidade; quais dados pessoais são necessários para a prestação do serviço; a forma como eles são coletados; se há o compartilhamento dos seus dados com terceiros; e quais as medidas de segurança implementadas para proteger os seus dados.",
      },
      {
        subtitle: "Conformidade Legal",
        text: "O Termo de Uso e a Política de Privacidade da AROAN - Eleeva IT foram elaborados em conformidade com a Lei Federal nº 12.965, de 23 de abril de 2014 (Marco Civil da Internet), e com a Lei Federal nº 13.709, de 14 de agosto de 2018 (Lei Geral de Proteção de Dados Pessoais).",
      },
    ],
  },
  {
    id: "principios-lgpd",
    icon: Scale,
    title: "Princípios da LGPD (Art. 6º)",
    content: [
      {
        subtitle: "Compromisso com a LGPD",
        text: "A AROAN - Eleeva IT se compromete a cumprir as normas previstas na Lei Geral de Proteção de Dados Pessoais (LGPD), e respeitar os princípios dispostos no art. 6º:",
        items: [
          "I – finalidade: realização do tratamento para propósitos legítimos, específicos, explícitos e informados ao titular, sem possibilidade de tratamento posterior de forma incompatível com essas finalidades;",
          "II – adequação: compatibilidade do tratamento com as finalidades informadas ao titular, de acordo com o contexto do tratamento;",
          "III – necessidade: limitação do tratamento ao mínimo necessário para a realização de suas finalidades, com abrangência dos dados pertinentes, proporcionais e não excessivos em relação às finalidades do tratamento de dados;",
          "IV – livre acesso: garantia, aos titulares, de consulta facilitada e gratuita sobre a forma e a duração do tratamento, bem como sobre a integralidade de seus dados pessoais;",
          "V – qualidade dos dados: garantia, aos titulares, de exatidão, clareza, relevância e atualização dos dados, de acordo com a necessidade e para o cumprimento da finalidade de seu tratamento;",
          "VI – transparência: garantia, aos titulares, de informações claras, precisas e facilmente acessíveis sobre a realização do tratamento e os respectivos agentes de tratamento, observados os segredos comercial e industrial;",
          "VII – segurança: utilização de medidas técnicas e administrativas aptas a proteger os dados pessoais de acessos não autorizados e de situações acidentais ou ilícitas de destruição, perda, alteração, comunicação ou difusão;",
          "VIII – prevenção: adoção de medidas para prevenir a ocorrência de danos em virtude do tratamento de dados pessoais;",
          "IX – não discriminação: impossibilidade de realização do tratamento para fins discriminatórios ilícitos ou abusivos;",
          "X – responsabilização e prestação de contas: demonstração, pelo agente, da adoção de medidas eficazes e capazes de comprovar a observância e o cumprimento das normas de proteção de dados pessoais e, inclusive, da eficácia dessas medidas.",
        ],
      },
    ],
  },
  {
    id: "aceitacao",
    icon: FileCheck,
    title: "Aceitação do Termo de Uso e Política de Privacidade",
    content: [
      {
        text: "Ao utilizar os serviços da AROAN, você confirma que leu, compreendeu o Termo de Uso e Política de Privacidade aplicáveis ao serviço solicitado e concorda em ficar a eles vinculado.",
      },
    ],
  },
  {
    id: "definicoes",
    icon: BookOpen,
    title: "Definições",
    content: [
      {
        text: "Para melhor compreensão deste documento, neste Termo de Uso e Política de Privacidade, consideram-se:",
        items: [
          "Agentes de tratamento: o controlador e o operador.",
          "Autoridade Nacional de Proteção de Dados (ANPD): órgão da administração pública responsável por zelar, implementar e fiscalizar o cumprimento desta Lei em todo o território nacional.",
          "Banco de dados: conjunto estruturado de dados pessoais, estabelecido em um ou em vários locais, em suporte eletrônico ou físico.",
          "Códigos maliciosos: qualquer programa de computador, ou parte de um programa, construído com a intenção de provocar danos, obter informações não autorizadas ou interromper o funcionamento de sistemas e/ou redes de computadores.",
          "Controlador: pessoa natural ou jurídica, de direito público ou privado, a quem competem as decisões referentes ao tratamento de dados pessoais.",
          "Cookies: arquivos armazenados nos computadores ou dispositivos móveis dos usuários ao acessar uma página web que guardam e recuperam informações relacionadas à sua navegação.",
          "Confidencialidade: garantia de que a informação é acessível somente por pessoas autorizadas.",
          "Integridade: garantia da exatidão e íntegra da informação e dos métodos de seu processamento.",
          "Dado pessoal: informação relacionada a pessoa natural identificada ou identificável.",
          "Dado pessoal sensível: dado pessoal sobre origem racial ou étnica, convicção religiosa, opinião política, filiação a sindicato ou a organização de caráter religioso, filosófico ou político, dado referente à saúde ou à vida sexual, dado genético ou biométrico, quando vinculado a uma pessoa natural.",
          "Encarregado: pessoa indicada pelo controlador e operador para atuar como canal de comunicação entre o controlador, os titulares dos dados e a Autoridade Nacional de Proteção de Dados (ANPD).",
          "Segurança da informação: conjunto de práticas e métodos voltados para a preservação da confidencialidade, integridade e disponibilidade da informação.",
          "Anonimização: utilização de meios técnicos razoáveis e disponíveis no momento do tratamento, por meio dos quais um dado perde a possibilidade de associação, direta ou indireta, a um indivíduo.",
          "Dado anonimizado: dado relativo a titular que não possa ser identificado, considerando a utilização de meios técnicos razoáveis e disponíveis na ocasião de seu tratamento.",
          "Endereço IP: Endereço de Protocolo de Internet, o código atribuído a um terminal de uma rede para permitir sua identificação, definido segundo parâmetros internacionais.",
          "Internet: o sistema constituído do conjunto de protocolos lógicos, estruturado em escala mundial para uso público e irrestrito, com a finalidade de possibilitar a comunicação de dados entre terminais por meio de diferentes redes.",
          "Lei Geral de Proteção de Dados Pessoais (LGPD): Lei Federal n. 13.709, de 14 de agosto de 2018, que dispõe sobre o tratamento de dados pessoais, inclusive nos meios digitais, por pessoa natural ou por pessoa jurídica de direito público ou privado, com o objetivo de proteger os direitos fundamentais de liberdade e de privacidade e o livre desenvolvimento da personalidade da pessoa natural.",
          "Operador: pessoa natural ou jurídica, de direito público ou privado, que realiza o tratamento de dados pessoais em nome do controlador.",
          "Sites e aplicativos: os sites são um conjunto de páginas disponíveis na internet, e os aplicativos são softwares que executam um grupo de funções com o objetivo de disponibilizar um serviço aos usuários.",
          "Terceiro: pessoa ou entidade que não participa diretamente em um contrato, em um ato jurídico ou em um negócio, ou que, para além das partes envolvidas, pode ter interesse num processo jurídico.",
          "Titular: pessoa natural a quem se referem os dados pessoais que são objeto de tratamento.",
          "Tratamento: toda operação realizada com dados pessoais, como as que se referem a coleta, produção, recepção, classificação, utilização, acesso, reprodução, transmissão, distribuição, processamento, arquivamento, armazenamento, eliminação, avaliação ou controle da informação, modificação, comunicação, transferência, difusão ou extração.",
          "Transferência internacional de dados: transferência de dados pessoais para país estrangeiro ou organismo internacional do qual o país seja membro.",
          "Uso compartilhado de dados: comunicação, difusão, transferência internacional, interconexão de dados pessoais ou tratamento compartilhado de bancos de dados pessoais por órgãos e entidades públicos no cumprimento de suas competências legais, ou entre esses e entes privados, reciprocamente, com autorização específica, para uma ou mais modalidades de tratamento permitidas por esses entes públicos, ou entre entes privados.",
          "Usuários: todas as pessoas naturais que utilizarem o serviço solicitado.",
        ],
      },
    ],
  },
  {
    id: "embasamento-legal",
    icon: Gavel,
    title: "Embasamento Legal",
    content: [
      {
        subtitle: "Acesso à Informação",
        text: "Há várias normas que dispõem sobre o acesso à informação e proteção aos dados pessoais, as quais são observadas pela AROAN - Eleeva IT no exercício de suas atividades. Primeiramente, destaca-se a Lei nº 12.527, de 18 de novembro de 2011, que regula o acesso a informações previsto na Constituição Federal.",
      },
    ],
  },
  {
    id: "atos-normativos",
    icon: Gavel,
    title: "Principais Atos Normativos Aplicáveis",
    content: [
      {
        text: "Principais atos normativos aplicáveis a serviços digitais, privacidade e proteção de dados pessoais:",
        items: [
          "Lei nº 12.965/2014: Marco Civil da Internet",
          "Lei nº 13.709/2018: LGPD",
          "Lei nº 14.129/2021: Governo Digital",
        ],
      },
    ],
  },
  {
    id: "coleta-dados",
    icon: Database,
    title: "Informações que Coletamos",
    content: [
      {
        text: "Coletamos informações fornecidas por você e dados coletados automaticamente durante o uso da plataforma.",
      },
    ],
  },
  {
    id: "seguranca",
    icon: Lock,
    title: "Segurança dos Dados",
    content: [
      {
        text: "Implementamos medidas técnicas e organizativas apropriadas para proteger seus dados pessoais.",
      },
    ],
  },
  {
    id: "direitos",
    icon: UserCheck,
    title: "Seus Direitos",
    content: [
      {
        text: "Você tem direitos garantidos pela LGPD, incluindo acesso, correção, eliminação e portabilidade dos dados.",
      },
    ],
  },
  {
    id: "contato",
    icon: Mail,
    title: "Contato",
    content: [
      {
        text: "Entre em contato conosco através de dpo@aroan.com.br",
      },
    ],
  },
];

const Privacidade = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Política de Privacidade (LGPD)"
        description="Como a Aroan coleta, usa, armazena e protege dados pessoais em conformidade com a LGPD, e como exercer seus direitos como titular."
        canonical="https://aroan.com.br/privacidade"
        ogType="website"
        article={false}
      />
      <Header />
      
      <main className="pt-24 pb-20">
        <section className="py-16 bg-gradient-to-br from-primary/10 via-background to-background border-b border-border">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Política de <span className="text-primary">Privacidade</span>
              </h1>
              <p className="text-lg text-muted-foreground">
                Comprometidos com a proteção dos seus dados e conformidade com LGPD.
              </p>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-5xl mx-auto space-y-16">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <section key={section.id} id={section.id}>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold">{section.title}</h2>
                  </div>
                  <div className="pl-16 space-y-6">
                    {section.content.map((content, i) => (
                      <div key={i} className="space-y-3">
                        {content.subtitle && <h3 className="text-xl font-semibold">{content.subtitle}</h3>}
                        {content.text && <p className="text-muted-foreground">{content.text}</p>}
                        {content.items && (
                          <ul className="space-y-2">
                            {content.items.map((item, j) => (
                              <li key={j} className="flex gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                                <span className="text-muted-foreground">{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </main>

      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          size="icon"
          className="fixed bottom-8 right-8 z-50 shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in"
          aria-label="Voltar ao topo"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      )}

      <Footer />
    </div>
  );
};

export default Privacidade;
